import {
  APP_DOMAIN,
  BOT_USERNAME,
  OPERATOR,
  OPERATOR_LINE,
  PAYMENT_PROVIDER,
  SERVICE_NAME,
  SITE_DOMAIN,
} from '../operator';
import type { LegalDoc } from '../types';

/*
 * Оферта по ст. 435 и 437 ГК РФ; акцепт — ст. 438. Условия подписки повторяют
 * `docs/subscription.html` (предупреждение за 3 дня, одно списание в конец
 * срока, отмена в профиле) и 376-ФЗ (с 01.03.2026: отдельное согласие на
 * автосписание, отказ в электронном виде). Поменялась логика в
 * `backend/src/billing/**` — поправить раздел 5. Реквизиты — последним
 * разделом: в приложении отдельного блока реквизитов нет, они здесь.
 */
export const offer: LegalDoc = {
  id: 'offer',
  title: 'Публичная оферта',
  description: `Договор на использование ${SERVICE_NAME}: бесплатный доступ, подписка, автопродление, отмена и возврат.`,
  blocks: [
    {
      p: `Договор на использование сервиса ${SERVICE_NAME}. Этот документ — публичная оферта (ст. 437 ГК РФ): предложение Исполнителя заключить договор на условиях ниже с любым дееспособным лицом (далее — «Пользователь»). Исполнитель — ${OPERATOR_LINE}.`,
    },

    { h: '1. Термины' },
    {
      p: `1.1. **Сервис** — Telegram-бот @${BOT_USERNAME}, мини-приложение в Telegram, сайт ${SITE_DOMAIN} и веб-приложение ${APP_DOMAIN}, в которых Пользователь практикует иностранный язык в разговоре с ИИ-репетитором.`,
    },
    {
      p: '1.2. **ИИ-репетитор** — программа на основе нейросетей, которая ведёт разговор, озвучивает ответы, исправляет ошибки и оценивает уровень языка. ИИ-репетитор не является человеком.',
    },
    { p: '1.3. **Подписка** — платный доступ к Сервису без ограничений бесплатного режима на оплаченный срок.' },

    { h: '2. Заключение договора' },
    {
      p: '2.1. Договор заключается, когда Пользователь запускает Сервис в Telegram или открывает веб-приложение и начинает им пользоваться (акцепт, ст. 438 ГК РФ). Условия подписки дополнительно принимаются при её оплате.',
    },
    {
      p: '2.2. Согласие на обработку персональных данных даётся отдельно — [[consent|текст согласия]]. Как обрабатываются данные — в [[privacy|Политике обработки персональных данных]].',
    },
    {
      p: '2.3. Сервис предназначен для лиц старше 18 лет. Несовершеннолетний пользуется Сервисом и оплачивает подписку только с согласия родителей или законных представителей; принимая оферту, Пользователь подтверждает, что ему исполнилось 18 лет или такое согласие у него есть. Исполнитель возраст не проверяет и сведений о возрасте не запрашивает.',
    },

    { h: '3. Что предоставляет Исполнитель' },
    {
      p: '3.1. Доступ к Сервису: разговоры с ИИ-репетитором голосом и текстом, исправление ошибок, словарь, проверку уровня языка, статистику занятий.',
    },
    {
      p: '3.2. Без подписки Сервис доступен с ограничениями — по числу ходов в разговоре и по функциям. Действующие ограничения видны в Сервисе.',
    },
    {
      p: '3.3. С подпиской ограничения бесплатного режима снимаются. Чтобы защитить Сервис от злоупотреблений, действует дневной лимит времени разговора, который при обычных занятиях не достигается.',
    },
    {
      p: '3.4. Ответы, исправления и оценки ИИ-репетитора формируются автоматически и могут содержать неточности и ошибки. Исполнитель не гарантирует их полноту и точность. Оценка уровня — ориентир для занятий, а не сертификат или официальный результат экзамена.',
    },
    {
      p: '3.5. Сервис — программа для самостоятельной языковой практики. Он не является образовательной услугой: не реализует образовательную программу, не проводит аттестацию и не выдаёт документов об образовании или квалификации.',
    },
    {
      p: '3.6. Исполнитель не гарантирует достижение конкретного уровня языка или иного результата: он зависит от регулярности и усилий Пользователя.',
    },
    {
      p: '3.7. ИИ-репетитор не даёт медицинских, юридических, финансовых, психологических и иных профессиональных консультаций. Всё, что он говорит на такие темы, — учебный разговор, а не совет, и решения на его основе Пользователь принимает сам.',
    },
    {
      p: '3.8. Исполнитель вправе менять, добавлять и убирать функции Сервиса, если оплаченная подписка при этом сохраняет основное назначение — разговорную практику с ИИ-репетитором.',
    },

    { h: '4. Стоимость и оплата' },
    {
      p: '4.1. Срок и стоимость подписки указаны в Сервисе на экране оплаты; действует цена на момент оплаты. Цены в рублях, НДС не облагаются: Исполнитель применяет налог на профессиональный доход (закон № 422-ФЗ).',
    },
    {
      p: `4.2. Оплата принимается через платёжного провайдера ${PAYMENT_PROVIDER} банковской картой или через СБП. Данные карты вводятся на странице провайдера; Исполнитель их не получает. Чек формируется в приложении «Мой налог» и направляется Пользователю в электронном виде.`,
    },
    {
      p: '4.3. Подписка действует календарный срок с момента оплаты: оплата 15-го числа в 10:00 даёт доступ до 10:00 15-го числа следующего месяца (если такого числа нет — до последнего дня месяца).',
    },

    { h: '5. Автопродление и его отмена' },
    {
      p: '5.1. Автопродление включается, только если при первой оплате Пользователь отдельно согласился на автоматическое списание и сохранение способа оплаты. Оплата через СБП без привязки и разовая оплата автопродление не включают.',
    },
    {
      p: '5.2. За 3 дня до конца срока Сервис присылает сообщение с датой, суммой и способом оплаты следующего списания — в Telegram, а если Telegram не подключён к учётной записи или сообщение туда не доставлено — на адрес электронной почты, указанный при оплате (если его нет — на адрес из Яндекс ID или VK ID, которым Пользователь вошёл).',
    },
    {
      p: '5.3. Списание проходит один раз — в момент окончания оплаченного срока, в сумме, указанной в уведомлении. Если банк отказал, повторных попыток нет: доступ к платным функциям приостанавливается, а продлить подписку можно вручную.',
    },
    {
      p: '5.4. Отключить автопродление можно в любой момент: в Сервисе — в разделе «Подписка» (в мини-приложении: Профиль → Подписка), или написав {contact}. Списания прекращаются сразу; оплаченный срок сохраняется до конца. Отказ от автосписаний с банковской карты также действует для всех последующих списаний с неё.',
    },

    { h: '6. Отказ от договора и возврат' },
    {
      p: '6.1. Пользователь вправе в любой момент отказаться от подписки (ст. 32 Закона «О защите прав потребителей»), написав {contact}. Исполнитель возвращает часть платы пропорционально неиспользованным полным дням срока за вычетом фактически понесённых расходов — комиссии платёжного провайдера за этот платёж. Возврат — в течение 10 дней со дня получения заявления тем же способом, которым была оплата.',
    },
    { p: '6.2. Использованный срок подписки не возвращается: доступ на этот срок предоставлен, услуга оказана.' },
    { p: '6.3. Этот раздел не ограничивает права Пользователя по ст. 29 и 32 Закона «О защите прав потребителей».' },

    { h: '7. Обязанности сторон' },
    { p: '7.1. Пользователь не должен:' },
    {
      ul: [
        'использовать Сервис для незаконных целей и для получения от ИИ-репетитора запрещённой информации;',
        'автоматически извлекать данные Сервиса, обходить ограничения и нагружать его программными средствами;',
        'передавать доступ к подписке третьим лицам и создавать несколько учётных записей, чтобы обойти ограничения бесплатного режима;',
        'использовать Сервис в объёме, явно превышающем личные занятия языком, в том числе для коммерческой обработки текстов или перепродажи ответов ИИ-репетитора;',
        'оплачивать подписку чужой картой или иным способом оплаты без согласия его владельца, а также совершать другие мошеннические действия с оплатой;',
        'сообщать в Сервисе персональные данные других людей.',
      ],
    },
    { p: '7.2. Пользователь сам отвечает за то, что отправляет в Сервис, и за то, как использует ответы ИИ-репетитора.' },
    {
      p: '7.3. При нарушении п. 7.1 Исполнитель вправе ограничить доступ к Сервису или отказаться от договора, уведомив Пользователя в Telegram или по электронной почте. Плата за неиспользованный срок при этом возвращается по правилам п. 6.1.',
    },
    {
      p: '7.4. Если использование Сервиса выглядит как злоупотребление или мошенничество — необычно большой объём запросов, автоматические обращения, признаки оплаты чужим способом, — Исполнитель вправе временно ограничить доступ на время проверки, уведомив Пользователя в Telegram или по электронной почте. Пользователь может дать пояснения в ответ на уведомление. Если нарушение не подтвердилось, доступ восстанавливается, а оплаченный срок продлевается на время ограничения.',
    },
    {
      p: '7.5. Исполнитель обеспечивает работу Сервиса, но не гарантирует её без перерывов: Сервис зависит от Telegram, провайдеров связи и сторонних ИИ-моделей. Перерывы на обслуживание и обновление не являются нарушением договора.',
    },

    { h: '8. Ответственность' },
    {
      p: '8.1. Стороны отвечают за неисполнение договора по законодательству РФ. Ответственность Исполнителя перед Пользователем-потребителем не ограничивается сверх того, что допускает закон.',
    },
    {
      p: '8.2. Исполнитель не отвечает за перебои, вызванные Telegram, банками, провайдерами связи, сторонними ИИ-моделями, решениями государственных органов и блокировками, на которые он не может повлиять, а также за обстоятельства непреодолимой силы.',
    },
    {
      p: '8.3. Исполнитель не отвечает за решения, которые Пользователь принял на основе ответов ИИ-репетитора (п. 3.7), и за последствия нарушения Пользователем п. 7.1.',
    },

    { h: '9. Споры' },
    {
      p: '9.1. Стороны решают споры переговорами. Претензия направляется {contact} с описанием требования; Исполнитель отвечает в течение 10 дней. Если спор не решён, он рассматривается в суде по правилам законодательства РФ.',
    },

    { h: '10. Изменение оферты' },
    {
      p: '10.1. Исполнитель может изменять оферту, публикуя новую редакцию на этой странице. Изменения цены не затрагивают уже оплаченный срок; о повышении цены автопродления Пользователь узнаёт не позднее чем за 3 дня до списания.',
    },

    { h: '11. Реквизиты Исполнителя' },
    {
      ul: [
        `Исполнитель: ${OPERATOR.name}`,
        `Статус: ${OPERATOR.status} (самозанятый)`,
        `ИНН: ${OPERATOR.inn}`,
        'Связь: {contact}',
      ],
    },
  ],
};
